import { useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useRouter } from "next/navigation";
import {
  useLazySyncronyGenerateTokenQuery,
  useSyncronyTransactionMutation,
} from "@/redux/apiSlices/synchronyApi";

const useSynchronyCheckout = () => {
  const { toast } = useToast();
  const router = useRouter();
  const partnerId = "PI10000828";

  const [transactionToken, setTransactionToken] = useState(null);
  const [getOrderID, setOrderID] = useState(null);

  const [syncronyTransaction, {
    isSuccess: isSyncronyTransactionSuccess,
    isError,
    error: errMsg,
  }] = useSyncronyTransactionMutation();

  const [triggerGenerateToken] = useLazySyncronyGenerateTokenQuery();

  useEffect(() => {
    if (isSyncronyTransactionSuccess) {
      toast({ title: "Payment approved" });
      router.push(`/success-order/${getOrderID}`);
    }

    if (isError) {
      toast({
        title: "Payment failed",
        description: errMsg?.data?.msg || "Transaction error",
        variant: "destructive",
      });
    }
  }, [isSyncronyTransactionSuccess, isError, errMsg, getOrderID, router, toast]);

  const handleTransactionLookup = async (token) => {
    try {
      await syncronyTransaction(token);
    } catch (error) {
      toast({
        title: "Transaction lookup failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const handleSynchronyClose = (event) => {
      const data = event.data;
      const modalClosed =
        (typeof data === "string" && (
          data === "Close Model" ||
          data === "Return To Merchant Shipping" ||
          data === "Close"
        )) ||
        (typeof data === "object" && data?.action === "setPayCloseModal");

      if (modalClosed) {
        console.log("🛑 SYF modal closed:", data);
        if (data?.action === "setPayCloseModal") {
          handleTransactionLookup(transactionToken);
        }
      }
    };

    window.addEventListener("message", handleSynchronyClose);
    return () => window.removeEventListener("message", handleSynchronyClose);
  }, [transactionToken]);

  const loadSYFScripts = (priceFormatted) => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    if (!document.getElementById("syfConfigScript")) {
      const configScript = document.createElement("script");
      configScript.id = "syfConfigScript";
      configScript.innerHTML = `
        var syfWidgetObject = {
          syfPartnerId: "${partnerId}",
          flowType: "PDP",
          initialAmount: "${priceFormatted}"
        };
      `;
      document.head.appendChild(configScript);
    }

    if (!document.getElementById("syfMPPScript")) {
      const script = document.createElement("script");
      script.id = "syfMPPScript";
      script.src = "https://spdpone.syfpos.com/mpp/UniFi.js";
      script.async = true;
      script.onload = () => console.log("✅ UniFi.js loaded");
      script.onerror = () => console.error("❌ Failed to load UniFi.js");
      document.head.appendChild(script);
    }
  };

  const openPaymentModal = (token, orderDetails, priceFormatted) => {
    const payload = {
      transactionToken: token,
      processInd: "3",
      tokenId: token,
      syfPartnerId: partnerId,
      clientTransId: `txn-${Date.now()}`,
      custZipCode: orderDetails?.address?.zipCode,
      custAddress1: orderDetails?.address?.address1,
      custCity: orderDetails?.address?.city,
      custState: orderDetails?.address?.state,
      transAmount1: priceFormatted,
      custFirstName: orderDetails?.address?.firstName,
      custLastName: orderDetails?.address?.lastName,
      phoneNumber: orderDetails?.address?.mobile,
    };

    console.log("🟢 Opening SYF modal with payload:", payload);
    window?.syfMPP?.calldBuyProcess(null, payload);
  };

  const waitForModal = (token, orderDetails, priceFormatted, tries = 0) => {
    if (tries > 5) {
      alert("Modal still not ready after 1.5s");
      return;
    }

    if (window?.syfMPP?.calldBuyProcess) {
      openPaymentModal(token, orderDetails, priceFormatted);
    } else {
      setTimeout(() => waitForModal(token, orderDetails, priceFormatted, tries + 1), 300);
    }
  };

  const synchronyCheckoutHandler = async (
    priceDetails,
    orderDetails,
    setIsProcessing,
    orderId
  ) => {
    if (!orderId) {
      toast({ title: "Missing order ID", variant: "destructive" });
      return;
    }

    setOrderID(orderId);
    setIsProcessing(true);

    const priceFormatted = priceDetails?.grandTotal?.toFixed(2) || "0.00";
    loadSYFScripts(priceFormatted);

    const { data, error } = await triggerGenerateToken(orderId);
    const token = data?.data?.transactionToken;

    if (!token || error) {
      toast({ title: "Failed to fetch transaction token", variant: "destructive" });
      setIsProcessing(false);
      return;
    }

    setTransactionToken(token);
    waitForModal(token, orderDetails, priceFormatted);
    setIsProcessing(false);
  };

  return { synchronyCheckoutHandler };
};

export default useSynchronyCheckout;
